import React, { useState } from 'lib/react';
import PropTypes from 'prop-types';
import { useTranslation } from 'lib/translation';
import { useSelector } from 'lib/redux';
import {
  EVButton,
  EVInputLabel,
  EVGrid,
  EVMenuItem,
  EVSelect,
  EVTypography,
} from 'lib/components';
import clsx from 'lib/clsx';
import TextInputField from 'commonComponents/textInputField';
import {
  COUNTRY_US, ZIP_CODE_US_REGEX, ZIP_CODE_CA_REGEX, US_STATES,
  CANADA_PROVINCES,
} from 'constants/AppConstants';
import BuildingDetailsStyle from './BuildingDetailsStyle';

/**
 * Component that renders form fields for updating building details
 */
function EditBuildingDetails(props) {
  const { t } = useTranslation();
  const {
    setEditingBuildingId, handleUpdate, buildingData, index,
  } = props;
  const classes = BuildingDetailsStyle();
  const inputFieldStyles = {
    FormHelperTextProps: { classes: { error: classes.inputHelperText } },
    InputProps: { classes: { root: classes.inputTextField } },
  };

  const [zipError, setZipError] = useState('');
  const [buildingDetails, setBuildingDetails] = useState(buildingData);
  const isComplex = useSelector((state) => state.order.isComplex);
  const mapPoints = useSelector((state) => state.orderAddresses.mapPoints);
  const country = useSelector((state) => state.orderAddresses.mapPoints[0].address.country);
  const statesList = country === COUNTRY_US ? US_STATES : CANADA_PROVINCES;
  const currentBuildingId = buildingDetails?.building;

  const handleZipCodeValidation = (value) => {
    const zipCodeRegex = country === COUNTRY_US ? ZIP_CODE_US_REGEX : ZIP_CODE_CA_REGEX;
    return zipCodeRegex.test(value);
  };

  const handleChange = (event) => {
    const { target: { id, value } } = event;
    setZipError('');
    setBuildingDetails({ ...buildingDetails, [id]: value });
  };

  const handleSelectOptionChange = (event) => {
    const { value } = event.target;
    setBuildingDetails({ ...buildingDetails, state: value });
  };

  const handleUpdateButton = (e) => {
    if (e) e.preventDefault();
    const zipCode = buildingDetails.zip;
    if (zipCode && !handleZipCodeValidation(zipCode)) {
      setZipError(t('order__valid_zip_code'));
    } else {
      handleUpdate(buildingDetails);
    }
  };
  const handleCancelClick = () => {
    setBuildingDetails(buildingData);
    // Flag as a user dismissal so the parent's auto-expand effect won't immediately re-open this
    // pin's edit form on the next mapPoints change while the same invalid address is still present.
    setEditingBuildingId(null, { dismissed: true });
  };

  const buildingIdExists = mapPoints.some(({ building }, i) => (building || String(i + 1)) === currentBuildingId && index !== i);
  const isDisabled = !(buildingDetails.zip && buildingDetails.city && buildingDetails.address)
  || (isComplex && !buildingDetails.building)
  || zipError
  || buildingIdExists;

  return (
    <EVGrid
      container
      direction="column"
      className={classes.buildingDetailsContainer}
      component="form"
      onSubmit={handleUpdateButton}
    >
      <EVTypography className={classes.editPropertyInfoHeading}>
        {t('EditPropertyInfo')}
      </EVTypography>
      <EVGrid>
        <TextInputField
          handleChange={handleChange}
          inputId="building"
          inputLabelProps={{ className: classes.editBuildingLabel }}
          inputValue={buildingDetails.building}
          labelText={t('BuildingNameId')}
          isRequired={isComplex}
          inputFieldProps={{ inputProps: { maxLength: 50 }, ...inputFieldStyles }}
          helperText={buildingIdExists && 'Building ID must be unique'}
        />
      </EVGrid>
      <EVGrid>
        <TextInputField
          handleChange={handleChange}
          inputId="address"
          inputLabelProps={{ className: classes.editBuildingLabel }}
          inputValue={buildingDetails.address}
          isRequired
          labelText={t('BuildingAddress')}
          inputFieldProps={inputFieldStyles}
        />
      </EVGrid>
      <EVGrid container wrap="nowrap" className={classes.editAddressRow}>
        <EVGrid style={{ flex: '1 1 auto' }}>
          <TextInputField
            handleChange={handleChange}
            inputId="city"
            inputLabelProps={{ className: classes.editBuildingLabel }}
            inputValue={buildingDetails.city}
            isRequired
            labelText={t('City')}
            inputFieldProps={inputFieldStyles}
          />
        </EVGrid>
        <EVGrid>
          <EVInputLabel className={classes.editBuildingLabel} id="state">
            {t('State')}
          </EVInputLabel>
          <EVSelect
            variant="outlined"
            id="state"
            value={buildingDetails.state}
            onChange={handleSelectOptionChange}
            inputProps={{ classes: { root: clsx(classes.inputTextField, 'select') } }}
          >
            {statesList.map(({ abbreviation }) => (
              // #genai: add stable identity key (Sonar S6477); state abbreviation is unique and stable, behavior unchanged
              <EVMenuItem key={abbreviation} value={abbreviation}>
                <em style={{ fontStyle: 'normal' }}>{abbreviation}</em>
              </EVMenuItem>
            ))}

          </EVSelect>
        </EVGrid>
        <EVGrid style={{ flex: '1 1 min-content' }}>
          <TextInputField
            handleChange={handleChange}
            inputId="zip"
            inputLabelProps={{ className: clsx(classes.editBuildingLabel, 'postalcode') }}
            inputValue={buildingDetails.zip}
            isRequired
            labelText={t('ZipCode')}
            inputFieldProps={{ inputProps: { maxLength: 10 }, ...inputFieldStyles }}
            helperText={zipError}
          />
        </EVGrid>
      </EVGrid>
      <EVGrid className={classes.editComponentButtonRow}>
        <EVButton
          id={`update-building-details-${index}`}
          disabled={isDisabled}
          variant="contained"
          color="primary"
          type="submit"
        >
          {t('Update')}
        </EVButton>
        <EVButton
          id={`cancel-edit-building-${index}`}
          onClick={handleCancelClick}
          variant="text"
          color="primary"
        >
          {t('common_cancel')}
        </EVButton>
      </EVGrid>
    </EVGrid>
  );
}

EditBuildingDetails.propTypes = {
  buildingData: PropTypes.shape().isRequired,
  setEditingBuildingId: PropTypes.func.isRequired,
  handleUpdate: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
};

export default EditBuildingDetails;
